/**
 * Meus dados.
 *
 * O cliente muda de número, corta o cabelo e quer trocar a foto, ou percebe que
 * a barbearia o chamou de "Joao" sem acento. Até aqui a única saída era pedir ao
 * barbeiro — que só olha o app entre um corte e outro, e que tem coisa melhor
 * para fazer do que digitar o telefone novo de outra pessoa.
 *
 * O formulário é um só e salva de uma vez, porque quem abre esta tela vem
 * corrigir uma coisa e ir embora. A foto é a exceção: sobe assim que é
 * escolhida, já que ela chega pela câmera e não pelo teclado — segurar a imagem
 * esperando um "Salvar" no rodapé só criaria a chance de perdê-la.
 */

import type { MyProfileView } from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScreenHeader } from '../../App.tsx';
import { Avatar, PhotoPicker } from '../../components/PhotoPicker.tsx';
import { Button, ErrorNote, Field, Spinner } from '../../components/ui.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { useTenant } from '../../lib/contexts.tsx';
import { phone as formatPhone } from '../../lib/format.ts';
import { PHOTO_RESIZE } from '../../lib/image.ts';

interface ProfileResponse {
  profile: MyProfileView;
}

/** Campos que têm lugar próprio para o erro, embaixo do input. */
const FORM_FIELDS = ['name', 'phone', 'username', 'birthDate', 'instagram'];

export function MyProfile() {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [instagram, setInstagram] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [saved, setSaved] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['my-profile'],
    queryFn: () => api.get<ProfileResponse>('/me'),
  });

  const profile = data?.profile;

  // O formulário nasce do que veio do servidor, uma vez só: recarregar os
  // campos a cada resposta apagaria o que a pessoa está digitando agora.
  useEffect(() => {
    if (!profile) return;
    setName(profile.name);
    setUsername(profile.username ?? '');
    // Formatado, e não o E.164 do banco: ninguém reconhece o próprio número
    // escrito como +5551999998888, e `phoneSchema` normaliza de volta o que
    // for digitado — inclusive o que sair daqui sem ninguém tocar.
    setPhone(formatPhone(profile.phone));
    setBirthDate(profile.client?.birthDate ?? '');
    setInstagram(profile.client?.instagram ?? '');
  }, [profile]);

  /** Tudo que muda o cadastro invalida a sessão: o nome aparece no cabeçalho. */
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['my-profile'] });
    await queryClient.invalidateQueries({ queryKey: ['session'] });
  };

  const save = useMutation({
    mutationFn: () =>
      api.patch<ProfileResponse>('/me', {
        name,
        username: username.trim() || null,
        phone,
        ...(profile?.client
          ? { birthDate: birthDate || null, instagram: instagram.trim() || null }
          : {}),
      }),
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await refresh();
    },
    onError: (caught) => {
      setSaved(false);
      setError(caught instanceof ApiError ? caught : null);
    },
  });

  const savePhoto = useMutation({
    mutationFn: (photo: string) => api.put<ProfileResponse>('/me/photo', { data: photo }),
    onSuccess: refresh,
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  const removePhoto = useMutation({
    mutationFn: () => api.delete<ProfileResponse>('/me/photo'),
    onSuccess: refresh,
  });

  if (isPending) return <Spinner />;
  if (!profile) return null;

  // O texto longo do conflito ("já está no cadastro de Fulano") é o que explica
  // o que fazer; o destaque no campo diz só que ele é o culpado. Por isso os
  // dois aparecem juntos, e a nota some quando o erro já está sob o campo.
  const inField = FORM_FIELDS.some((field) => error?.fieldError(field));
  const general = error && (error.status === 409 || !inField) ? error.message : null;

  return (
    <div className="flex min-h-full flex-col">
      <ScreenHeader title="Meus dados" subtitle={`Seu cadastro na ${tenant.name}`} />

      {/* O espaço embaixo é o do botão fixo: sem ele, o último campo fica
          atrás da barra e ninguém alcança o que digitou. */}
      <div className="flex flex-col gap-5 px-4 pt-5 pb-8">
        {profile.client && (
          <section className="flex items-center gap-4">
            <Avatar name={profile.client.name} photoUrl={profile.client.photoUrl} size={72} />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <PhotoPicker
                label={profile.client.photoUrl ? 'Trocar foto' : 'Adicionar foto'}
                options={PHOTO_RESIZE}
                facing="user"
                busy={savePhoto.isPending}
                onPicked={(photo) => savePhoto.mutateAsync(photo)}
              />
              {profile.client.photoUrl && (
                <button
                  type="button"
                  onClick={() => removePhoto.mutate()}
                  className="touch-target self-start text-[13px] text-ink-faint"
                >
                  Remover foto
                </button>
              )}
            </div>
          </section>
        )}

        <Field
          label="Nome"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setSaved(false);
          }}
          autoCapitalize="words"
          error={error?.fieldError('name')}
        />

        <Field
          label="Telefone"
          value={phone}
          onChange={(event) => {
            setPhone(event.target.value);
            setSaved(false);
          }}
          inputMode="tel"
          placeholder="(51) 99999-8888"
          hint="É com ele que você entra no app e que a barbearia te encontra"
          error={error?.fieldError('phone')}
        />

        <Field
          label="Usuário (opcional)"
          value={username}
          onChange={(event) => {
            setUsername(event.target.value);
            setSaved(false);
          }}
          autoCapitalize="none"
          autoCorrect="off"
          hint="Um apelido para entrar sem digitar o telefone"
          error={error?.fieldError('username')}
        />

        {profile.client && (
          <>
            <Field
              label="Data de nascimento"
              type="date"
              value={birthDate}
              onChange={(event) => {
                setBirthDate(event.target.value);
                setSaved(false);
              }}
              error={error?.fieldError('birthDate')}
            />

            <Field
              label="Instagram"
              value={instagram}
              onChange={(event) => {
                setInstagram(event.target.value);
                setSaved(false);
              }}
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="@perfil"
              error={error?.fieldError('instagram')}
            />
          </>
        )}

        {general && <ErrorNote>{general}</ErrorNote>}

        <button
          type="button"
          onClick={() => navigate('/ajustes')}
          className="touch-target self-start text-[14px] text-accent"
        >
          Trocar a senha em Ajustes
        </button>
      </div>

      {/* Fundo sólido de propósito: barra fixa translúcida deixa o texto da
          tela passar por baixo do botão enquanto a página rola. */}
      <div className="pb-safe sticky bottom-0 z-40 mt-auto border-t border-line bg-surface px-4 pt-3 pb-3">
        <Button full loading={save.isPending} onClick={() => save.mutate()}>
          {saved ? 'Tudo salvo' : 'Salvar'}
        </Button>
      </div>
    </div>
  );
}
